import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Calendar, Clock, Search, UserRound } from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import BookingPayment from "@/components/app/BookingPayment";

export default function FollowUpFlow({ patient, onReset, onBack }) {
  const { t } = useLang();
  const [departments, setDepartments] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [picked, setPicked] = useState(null);

  useEffect(() => {
    api.departments().then(setDepartments);
    api.doctors().then(setDoctors);
  }, []);

  const filtered = doctors.filter((d) => {
    const matchesQ = (d.name + " " + d.department).toLowerCase().includes(query.toLowerCase());
    const matchesF = filter === "all" || d.department === filter;
    return matchesQ && matchesF;
  });

  if (picked) {
    return <BookingPayment patient={patient} doctor={picked} consultationType="follow_up" onReset={onReset} onBack={() => setPicked(null)} />;
  }

  return (
    <div className="mx-auto max-w-6xl py-6">
      <Button variant="ghost" onClick={onBack} data-testid="back-btn" className="mb-4">
        <ArrowLeft className="mr-1.5 h-4 w-4" /> {t("back")}
      </Button>
      <h2 className="mb-6 text-2xl font-bold tracking-tight sm:text-3xl">{t("select_doctor")}</h2>

      <div className="mb-5 flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("search_doctor")}
            className="pl-10"
            data-testid="doctor-search-input"
          />
        </div>
      </div>

      <Tabs value={filter} onValueChange={setFilter} className="mb-6">
        <TabsList className="flex flex-wrap h-auto bg-slate-100 p-1" data-testid="dept-tabs">
          <TabsTrigger value="all" data-testid="tab-all">{t("all_depts")}</TabsTrigger>
          {departments.map((d) => (
            <TabsTrigger key={d.id} value={d.id} data-testid={`tab-${d.id}`}>
              {d.name}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
        {filtered.map((d) => (
          <Card key={d.id} className="group overflow-hidden border-slate-200 bg-white p-5 transition hover:shadow-xl" data-testid={`doctor-card-${d.id}`}>
            <div className="flex items-start gap-4">
              {d.photo ? (
                <img src={d.photo} alt="" className="h-16 w-16 rounded-full object-cover ring-2 ring-sky-100" />
              ) : (
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-sky-100">
                  <UserRound className="h-8 w-8 text-sky-600" />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <h3 className="font-bold text-slate-900">{d.name}</h3>
                <div className="mt-1 flex items-center gap-2">
                  <Badge variant="secondary" className="bg-sky-100 text-sky-800">
                    {departments.find((x) => x.id === d.department)?.name || d.department}
                  </Badge>
                </div>
                <div className="mt-3 text-sm font-semibold text-slate-700">
                  {t("fee")}: ₹{d.fee}
                </div>
              </div>
            </div>
            <Button
              onClick={() => setPicked(d)}
              data-testid={`book-doctor-${d.id}-btn`}
              className="mt-4 w-full bg-sky-600 hover:bg-sky-700"
            >
              <Calendar className="mr-1.5 h-4 w-4" /> {t("select_slot")}
            </Button>
          </Card>
        ))}
        {filtered.length === 0 && (
          <Card className="col-span-full p-8 text-center text-slate-500">No doctors match your search.</Card>
        )}
      </div>
    </div>
  );
}
